// The squad on the map (R1571; mockup round 075, cards 16 to 19;
// ui-map-interface.md §8).
//
// R199 made the squad mark the real squad: the clan members whose
// `ClanMember.squad` is the reader's own `ClanInfo.squad`. Nothing named
// that squad, so a commander who moved to another squad saw the marks
// move with no message. Four pieces answer it, and this module holds the
// rules and the markup of all four; the overlay places them and the feed
// calls the watch.
//
//   - The squad chip (card 16): the reader's squad fleet mark as the
//     icon. The dock head of a desktop draws it with the clan tag, the
//     clan name, the squad number and the head count (`ui/overlay/
//     thumbdock.ts`, R1767). A press opens the roster card.
//   - The marks key (card 17): the three degrees of R185, hulls and holds,
//     drawn from the reader's own Marks theme (`render/squad-key.ts`).
//   - The squad change notice (card 18): one notice, one pulse of the
//     chip and one lift of the new squad's hulls, when a clan frame moves
//     the reader to another squad.
//   - The pickup squad (card 19): an NPC clan squad. The chip names no
//     clan, and the first clan frame of a season says what the squad is.
//
// **The count is every member of the squad, the reader included**, so the
// chip counts the hulls that wear the mark plus the reader's own. Squad 0
// is "unassigned" (R199) and has no mark, so it draws no chip.

import type { ClanInfo, ClanMember } from '../gen/proto/aquaphobia';
import { siege as W, squad as S } from '../gen/strings';
import type { HoldEra, HoldSymbolId } from '../render/hold-marks';
import type { SceneLook } from '../render/scene-look';
import type { KeyRank } from '../render/squad-key';
import { otherSquadValue } from '../render/squad-key';
import type { SpawnKind } from '../render/spawn-plan';
import type { OwnHoldStyle, SquadFleetStyle, SquadHoldStyle } from './settings';
import { esc } from './esc';
import { readStore, writeStore } from './localstore';
import { pipsHtml, readinessWord, type Readiness } from './siegewindow';

/** The reader's squad, as the chip, the roster and the key read it. */
export interface SquadView {
  clanId: number;
  /** The squad number. */
  squad: number;
  /** A squad of an NPC clan: a pickup squad (player-reach.md §5.2). */
  pickup: boolean;
  /** The clan's name, and '' for a pickup squad: an NPC clan is no clan
   *  of people, so the chrome never prints its name (card 19). */
  clanName: string;
  /** The members of the squad in slot order, the reader included. */
  members: ClanMember[];
}

/**
 * The reader's squad off the clan frame, or null for no clan and for
 * squad 0. The members come in slot order: the account id, the order
 * `squadSlotsOf` numbers them in (R830).
 */
export function squadView(clan: ClanInfo | null): SquadView | null {
  if (!clan || clan.id === 0 || clan.squad === 0) return null;
  const members = clan.members.filter((m) => m.squad === clan.squad)
    .sort((a, b) => a.accountId - b.accountId);
  return {
    clanId: clan.id, squad: clan.squad, pickup: clan.npc,
    clanName: clan.npc ? '' : clan.name, members,
  };
}

// ---------------------------------------------------------------------------
// The watch: when the squad changed
// ---------------------------------------------------------------------------

/** What one clan frame means to the squad. */
export type SquadEvent =
  /** The first clan frame after a login or a season hello. It is silent. */
  | { kind: 'baseline'; view: SquadView | null }
  /** The reader's clan or squad number is not what the last frame said. */
  | { kind: 'changed'; view: SquadView | null };

/** The key a change is judged on: the clan and the squad, or none. */
function keyOf(v: SquadView | null): string {
  return v === null ? 'none' : `${v.clanId}:${v.squad}`;
}

/**
 * Tells a squad change from a repeat (card 18).
 *
 * A login hello and a season hello arm a BASELINE: the server sends the
 * clan frame right after both (`join.go`, the roll of `season.go`), so the
 * first frame after them states the squad and is no change. The season
 * roll moves every squad at once, and the welcome of the season names the
 * squad, so the roll is silent too. A RESYNC hello keeps the baseline: the
 * server sends no clan frame after a resync, and the squad has not moved.
 */
export class SquadWatch {
  private key: string | null = null;

  /** A hello arrived. `kind` is the arrival the feed named. */
  hello(kind: SpawnKind): void {
    if (kind !== 'resync') this.key = null;
  }

  /** A clan frame arrived. Null for a repeat frame. */
  clan(info: ClanInfo): SquadEvent | null {
    const view = squadView(info.id === 0 ? null : info);
    const key = keyOf(view);
    if (this.key === null) {
      this.key = key;
      return { kind: 'baseline', view };
    }
    if (key === this.key) return null;
    this.key = key;
    return { kind: 'changed', view };
  }
}

/** The line of the squad change notice: the head, then the body. */
export function changeNotice(v: SquadView | null): string {
  if (v === null) return `${S.changed_head} · ${S.changed_none}`;
  const count = v.members.length;
  const body = v.pickup ? S.changed_pickup({ count }) : S.changed_squad({ n: v.squad, count });
  return `${S.changed_head} · ${body}`;
}

/** The line of the pickup squad notice (card 19). */
export function pickupNotice(v: SquadView): string {
  return `${S.pickup_head} · ${S.pickup_body({ count: v.members.length })}`;
}

/** The browser memory of the pickup notice: the last world, season and
 *  account it was shown for. */
export const PICKUP_NOTICE_KEY = 'aqua.squad.pickupNotice';

/**
 * True when the pickup notice is due: the first clan frame of a season
 * names a pickup squad, and this browser has not shown the notice for
 * this world, season and account. It marks the notice as shown. A store
 * that refuses forgets, and the notice then shows on each login.
 */
export function pickupNoticeDue(
  v: SquadView | null, worldId: number, season: number, accountId: number,
): boolean {
  if (v === null || !v.pickup) return false;
  const stamp = `${worldId}:${season}:${accountId}`;
  if (readStore(PICKUP_NOTICE_KEY) === stamp) return false;
  writeStore(PICKUP_NOTICE_KEY, stamp);
  return true;
}

// ---------------------------------------------------------------------------
// The markup
// ---------------------------------------------------------------------------

/** The head of the roster card and of the key: the clan, then the squad. */
function headWords(v: SquadView): { name: string; clan: string } {
  return v.pickup
    ? { name: S.chip_pickup, clan: '' }
    : { name: S.chip_squad({ n: v.squad }), clan: esc(v.clanName) };
}

/** The word on the right of one roster row. */
function rosterState(m: ClanMember, self: number): string {
  if (m.accountId === self) return S.roster_you;
  if (m.reserve) return S.roster_reserve;
  return m.online ? S.roster_online : S.roster_at_sea;
}

/**
 * The roster card under the chip (card 16): the squad and the clan, one
 * row a member with the reader first, then MARKS KEY and OPEN CLAN. The
 * world server holds no clan role, so the right of a row says who the
 * member is to the reader, in the CLAN tab's own words (R1571).
 */
export function rosterHtml(v: SquadView, self: number, siege?: RosterSiege): string {
  const head = headWords(v);
  const rows = [...v.members].sort((a, b) => (a.accountId === self ? -1 : b.accountId === self ? 1 : 0));
  // With the siege parts each row states the member's readiness for the
  // reader's window as pips and a word, and RALLY under the window line is
  // the one primary of the card (the game critique of 2026-10-14).
  const state = (m: ClanMember) => {
    if (!siege) return rosterState(m, self);
    const word = readinessWord(siege.ready(m));
    return m.accountId === self ? W.ready_you({ state: word }) : word;
  };
  const rally = siege !== undefined && siege.window.includes('data-act="rally"');
  return `<div class="sqhead">`
    + (head.clan ? `<span class="sqclan">${head.clan}</span>` : '')
    + `<span class="sqname">${head.name}</span></div>`
    + (siege ? siege.window : '')
    + rows.map((m) => `<div class="sqrow${m.accountId === self ? ' you' : ''}">`
      + (siege ? pipsHtml(siege.ready(m)) : '')
      + `<span class="nm">${esc(m.name)}</span><span class="st">${state(m)}</span></div>`).join('')
    + `<div class="acts"><button data-act="squadkey">${S.marks_key}</button>`
    + `<button${rally ? '' : ' class="primary"'} data-act="squadclan">${S.open_clan}</button></div>`;
}

/** The siege parts of the roster card (`ui/siegewindow.ts`): the
 *  readiness of a member and the window line with RALLY. */
export interface RosterSiege {
  ready: (m: ClanMember) => Readiness;
  window: string;
}

/** What the key draws its marks from: the reader's look and hold symbol. */
export interface SquadLook {
  scene: SceneLook;
  holdSymbol: HoldSymbolId;
  era: HoldEra;
}

/**
 * The pictures of the key's marks, as `<img>` markup at their CSS size,
 * and '' where the page has no 2D canvas (`ui/squadmarks.ts`).
 */
export interface KeyPictures {
  /** One hull of a degree. `squadFleet` draws the mate under another value. */
  hull(rank: KeyRank, squadFleet?: SquadFleetStyle): string;
  /** One hold of a degree. */
  hold(rank: KeyRank): string;
}

const OWN_HOLD_WORDS: Record<OwnHoldStyle, string> = {
  'ground-plate': S.own_hold_ground_plate,
  'beam-column': S.own_hold_beam_column,
  'crown-chevrons': S.own_hold_crown_chevrons,
  'cross-rays': S.own_hold_cross_rays,
  'breathing-lozenge': S.own_hold_breathing_lozenge,
  'landing-pillars': S.own_hold_landing_pillars,
};

const SQUAD_HOLD_WORDS: Record<SquadHoldStyle, string> = {
  'half-plate': S.squad_hold_half_plate,
  'no-mark': S.squad_hold_no_mark,
  'single-chevron': S.squad_hold_single_chevron,
  'waterline-ticks': S.squad_hold_waterline_ticks,
  'short-column': S.squad_hold_short_column,
  'small-lozenge': S.squad_hold_small_lozenge,
};

/** What the hold column says under the reader's look, for one degree. */
export function holdWords(look: SceneLook, rank: KeyRank): string {
  if (rank === 'own') return OWN_HOLD_WORDS[look.ownHold];
  if (rank === 'mate') return SQUAD_HOLD_WORDS[look.squadHold];
  const plate = look.ownHold === 'ground-plate' || look.squadHold === 'half-plate';
  return plate ? S.faction_hold_no_plate : S.faction_hold_no_mark;
}

/**
 * The marks key (card 17): one row for each degree of R185, the hull and
 * its words on the left and the hold and its words on the right, and a
 * foot that draws the other hull value of R185. `faction` is the reader's
 * faction name.
 */
export function keyHtml(v: SquadView, look: SceneLook, faction: string, pics: KeyPictures): string {
  const head = headWords(v);
  const count = v.members.length;
  const row = (rank: KeyRank, who: string, sub: string, hold: string) =>
    `<div class="krow k-${rank}">`
    + `<span class="km">${pics.hull(rank)}</span>`
    + `<span class="kw"><b>${who}</b><i>${sub}</i></span>`
    + `<span class="km">${pics.hold(rank)}</span>`
    + `<span class="kw"><b>${hold}</b><i>${holdWords(look, rank)}</i></span></div>`;
  const other = otherSquadValue(look);
  return `<div class="khead"><span class="kt">${S.key_head}</span>`
    + `<span class="ks">${head.name}${head.clan ? ` · ${head.clan}` : ''}</span></div>`
    + row('own', S.key_you, S.key_your_fleets, S.key_your_hold)
    + row('mate', head.name, S.key_count({ count }), S.key_squad_hold)
    + row('faction', esc(faction.toUpperCase()), S.key_rest, S.key_faction_hold)
    + `<div class="kfoot"><span>${S.key_foot({ value: look.squadFleet, other })}</span>`
    + `<span class="km">${pics.hull('mate', other)}</span></div>`;
}
